import { ReactElement, CSSProperties, memo } from 'react'
import PropTypes from 'prop-types'
import { animated } from 'react-spring'
import { useRouter } from 'next/router'
import { FaPhoneAlt } from 'react-icons/fa'

import { common } from '@/features/landing/constants'
import { ReactComponent as FacebookIcon } from '@/svg/icon-facebook.svg'
import { ReactComponent as Logo } from '@/svg/logo_logo.svg'

import ScrollLink from '@/lib/scroll'

import routes from '@/routes'

import { NavigatorGroup, MenuList } from './components/molecules'
import { Hamburger } from './components/atoms'
import Container from './style'
import { useHooks } from './methods'

interface Props {
  style?: CSSProperties
  className?: string
  id?: string
}

function NavigationBar({ id, className, style }: Props): ReactElement {
  const {
    isPressed,
    handlePressed,
    backgroundSpring,
    menuListSpring,
    hideMenu,
    containerRef,
  } = useHooks(routes.length + 1)

  const { pathname } = useRouter()

  return (
    <Container id={id} className={className} style={style}>
      <nav className="container">
        <ScrollLink
          url={{
            pathname: '/',
            hash: pathname === '/' ? 'top' : '',
          }}
          scrollOption={{ hashSpy: false }}
        >
          <figure className="logo">{<Logo />}</figure>
        </ScrollLink>
        <section className="nav-bar">
          <section className="menu-container">
            <NavigatorGroup routes={routes} />
            <a
              className="facebook-share-button"
              href={common.facebookPage}
              rel="noreferrer"
              target="_blank"
            >
              <FacebookIcon />
            </a>
            <a className="phone-button" href={common.tel}>
              <FaPhoneAlt size="24px" />
            </a>
          </section>
          <Hamburger
            className={`${isPressed ? 'pressed' : ''} hamburgur`}
            onClick={handlePressed}
            isClicked={isPressed}
          />
        </section>
      </nav>
      <animated.section
        className="menu-list"
        onClick={hideMenu}
        style={{
          ...menuListSpring,
          display: backgroundSpring.opacity.to((value: number) =>
            value > 0.005 ? 'block' : 'none',
          ),
        }}
      >
        <MenuList routes={routes} onPress={hideMenu} />
        <a
          className="facebook-share-button-inlist"
          href={common.facebookPage}
          rel="noreferrer"
          target="_blank"
        >
          <FacebookIcon />
        </a>
        <a className="phone-button-inlist" href={common.tel}>
          <FaPhoneAlt size="20px" />
        </a>
      </animated.section>
      <animated.figure
        className="background"
        onClick={hideMenu}
        style={{
          opacity: backgroundSpring.opacity,
          display: backgroundSpring.opacity.to((value: number) =>
            value > 0.005 ? 'block' : 'none',
          ),
        }}
        ref={containerRef}
      ></animated.figure>
    </Container>
  )
}

NavigationBar.propTypes = {
  style: PropTypes.object,
  className: PropTypes.string,
  id: PropTypes.string,
}

NavigationBar.defaultProps = {
  className: '',
}

export default memo(NavigationBar)
